import { AIActionSchema, IdSchema } from '@aibi/contracts';
import { z } from 'zod';
import { AI_ERROR_CODES } from './errors.js';

/** Maximo absoluto de acciones del protocolo; `AICoreLimits` puede ser mas estricto. */
export const MAX_PROTOCOL_ACTIONS = 50;

const CandidateSchema = z.strictObject({
  id: IdSchema,
  label: z.string().min(1).max(200),
  kind: z.enum(['dimension', 'measure', 'member', 'visualization', 'filter']),
  dimensionId: IdSchema.optional(),
});

/**
 * Unica forma en que un proveedor puede responder al AI Core: JSON estricto con
 * acciones del catalogo AI Action, una pregunta de aclaracion o un error. Cualquier
 * otra cosa (texto libre, SQL, codigo, propiedades extra) se rechaza.
 */
export const AIModelOutputSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('actions'),
    actions: z.array(AIActionSchema).min(1).max(MAX_PROTOCOL_ACTIONS),
    rationale: z.string().max(2000).optional(),
  }),
  z.strictObject({
    kind: z.literal('clarification'),
    question: z.string().min(1).max(500),
    reason: z.string().min(1).max(500),
    candidates: z.array(CandidateSchema).min(1).max(20),
    affectedActionIds: z.array(IdSchema).max(MAX_PROTOCOL_ACTIONS).default([]),
    term: z.string().min(1).max(200).optional(),
  }),
  z.strictObject({
    kind: z.literal('error'),
    code: z.enum(AI_ERROR_CODES),
    message: z.string().min(1).max(500),
    candidates: z.array(CandidateSchema).max(20).optional(),
  }),
]);
export type AIModelOutput = z.infer<typeof AIModelOutputSchema>;
export type AIModelOutputInput = z.input<typeof AIModelOutputSchema>;

let cachedSchema: Readonly<Record<string, unknown>> | undefined;

/** JSON Schema (draft 2020-12) de la respuesta esperada; se envia a cada proveedor. */
export function aiResponseJsonSchema(): Readonly<Record<string, unknown>> {
  cachedSchema ??= Object.freeze({
    ...z.toJSONSchema(AIModelOutputSchema, { target: 'draft-2020-12', io: 'input' }),
    $id: 'urn:aibi:ai-core:model-output:1',
    title: 'ai-model-output',
  });
  return cachedSchema;
}
